import { wsClient } from './websocket';

export interface PeerConnection {
  peerConnection: RTCPeerConnection;
  userId: string;
  stream?: MediaStream;
  videoElement?: HTMLVideoElement;
}

export interface CallState {
  callId: string | null;
  isInCall: boolean;
  isCallActive: boolean;
  localStream: MediaStream | null;
  remoteStreams: Map<string, MediaStream>;
  peers: Map<string, RTCPeerConnection>;
  isMuted: boolean;
  isVideoEnabled: boolean;
  participants: Map<string, { userId: string; username: string }>;
}

// STUN servers for NAT traversal
const STUN_SERVERS: RTCConfiguration = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
  ],
};

export class WebRTCManager {
  private callState: CallState = {
    callId: null,
    isInCall: false,
    isCallActive: false,
    localStream: null,
    remoteStreams: new Map(),
    peers: new Map(),
    isMuted: false,
    isVideoEnabled: true,
    participants: new Map(),
  };

  private callStateListeners: Set<(state: CallState) => void> = new Set();
  private currentRoomId: string | null = null;
  private currentUserId: string | null = null;

  constructor() {
    this.setupWebSocketListeners();
  }

  private setupWebSocketListeners(): void {
    // Call request received
    wsClient.onCallRequest((data) => {
      console.log('Call request received:', data);
      // Handle incoming call request (show notification, etc.)
      this.notifyStateChange();
    });

    // Call accepted
    wsClient.onCallAccepted((data) => {
      console.log('Call accepted:', data);
      this.callState.callId = data.callId;
      this.callState.isInCall = true;
      this.callState.isCallActive = true;
      // Create offer for the accepted call
      if (data.calleeId && data.calleeId !== this.currentUserId) {
        this.createOffer(data.calleeId).catch((error) => {
          console.error('Error creating offer after call accepted:', error);
        });
      }
      this.notifyStateChange();
    });

    // Call rejected
    wsClient.onCallRejected((data) => {
      console.log('Call rejected:', data);
      this.endCall();
    });

    // Call ended
    wsClient.onCallEnded((data) => {
      console.log('Call ended:', data);
      this.endCall();
    });

    // WebRTC Offer received
    wsClient.onWebRTCOffer(async (data) => {
      console.log('WebRTC offer received:', data);
      await this.handleOffer(data);
    });

    // WebRTC Answer received
    wsClient.onWebRTCAnswer(async (data) => {
      console.log('WebRTC answer received:', data);
      await this.handleAnswer(data);
    });

    // ICE candidate received
    wsClient.onWebRTCIceCandidate(async (data) => {
      console.log('ICE candidate received:', data);
      await this.handleIceCandidate(data);
    });

    // Call error
    wsClient.onCallError((data) => {
      console.error('Call error:', data);
      this.endCall();
    });
  }

  setCurrentUser(userId: string): void {
    this.currentUserId = userId;
  }

  setCurrentRoom(roomId: string): void {
    this.currentRoomId = roomId;
  }

  subscribe(listener: (state: CallState) => void): () => void {
    this.callStateListeners.add(listener);
    return () => {
      this.callStateListeners.delete(listener);
    };
  }

  private notifyStateChange(): void {
    this.callStateListeners.forEach((listener) => {
      listener({ ...this.callState });
    });
  }

  getState(): CallState {
    return { ...this.callState };
  }

  async startCall(targetUserId: string): Promise<void> {
    if (!this.currentRoomId || !this.currentUserId) {
      throw new Error('Room or user not set');
    }

    try {
      // Request media access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      this.callState.localStream = stream;
      this.callState.isVideoEnabled = true;
      this.callState.isMuted = false;

      // Request call via WebSocket
      wsClient.requestCall(targetUserId, this.currentRoomId);

      // Set up one-time listener for call initiated
      const handleCallInitiated = (data: { callId: string; targetUserId: string }) => {
        this.callState.callId = data.callId;
        this.callState.isInCall = true;
        // Create peer connection and offer once we have the callId
        this.createOffer(targetUserId).catch((error) => {
          console.error('Error creating offer after call initiated:', error);
        });
        this.notifyStateChange();
        // Remove listener after use
        wsClient.offCallInitiated(handleCallInitiated);
      };
      
      wsClient.onCallInitiated(handleCallInitiated);

      this.notifyStateChange();
    } catch (error) {
      console.error('Error starting call:', error);
      throw error;
    }
  }

  async joinRoomCall(roomId: string): Promise<void> {
    if (!this.currentUserId) {
      throw new Error('User not set');
    }

    try {
      // Request media access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      this.callState.localStream = stream;
      this.callState.isVideoEnabled = true;
      this.callState.isMuted = false;
      this.callState.isInCall = true;
      this.callState.isCallActive = true;
      this.currentRoomId = roomId;

      // Create peer connections for all participants in the room
      // This is a simplified version - in production, you'd get the list from the server
      this.notifyStateChange();
    } catch (error) {
      console.error('Error joining room call:', error);
      throw error;
    }
  }

  async acceptCall(callId: string): Promise<void> {
    try {
      // Request media access
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });

      this.callState.localStream = stream;
      this.callState.callId = callId;
      this.callState.isInCall = true;
      this.callState.isCallActive = true;
      this.callState.isVideoEnabled = true;
      this.callState.isMuted = false;

      wsClient.acceptCall(callId);
      this.notifyStateChange();
    } catch (error) {
      console.error('Error accepting call:', error);
      throw error;
    }
  }

  rejectCall(callId: string): void {
    wsClient.rejectCall(callId);
    this.endCall();
  }

  async handleOffer(data: {
    callId: string;
    offer: RTCSessionDescriptionInit;
    fromUserId: string;
  }): Promise<void> {
    if (!this.callState.localStream) {
      // Request media if not already available
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      });
      this.callState.localStream = stream;
    }

    const peerConnection = this.createPeerConnection(data.fromUserId);
    this.callState.peers.set(data.fromUserId, peerConnection);

    // Add local stream tracks
    this.callState.localStream?.getTracks().forEach((track) => {
      peerConnection.addTrack(track, this.callState.localStream!);
    });

    await peerConnection.setRemoteDescription(new RTCSessionDescription(data.offer));

    const answer = await peerConnection.createAnswer();
    await peerConnection.setLocalDescription(answer);

    wsClient.sendAnswer(data.callId, answer, data.fromUserId);
    this.notifyStateChange();
  }

  async handleAnswer(data: {
    callId: string;
    answer: RTCSessionDescriptionInit;
    fromUserId: string;
  }): Promise<void> {
    const peerConnection = this.callState.peers.get(data.fromUserId);
    if (!peerConnection) {
      console.error('Peer connection not found for user:', data.fromUserId);
      return;
    }

    await peerConnection.setRemoteDescription(new RTCSessionDescription(data.answer));
    this.notifyStateChange();
  }

  async handleIceCandidate(data: {
    callId: string;
    candidate: RTCIceCandidateInit;
    fromUserId: string;
  }): Promise<void> {
    const peerConnection = this.callState.peers.get(data.fromUserId);
    if (!peerConnection) {
      console.error('Peer connection not found for user:', data.fromUserId);
      return;
    }

    try {
      await peerConnection.addIceCandidate(new RTCIceCandidate(data.candidate));
    } catch (error) {
      console.error('Error adding ICE candidate:', error);
    }
  }

  private createPeerConnection(userId: string): RTCPeerConnection {
    const peerConnection = new RTCPeerConnection(STUN_SERVERS);

    // Handle remote stream
    peerConnection.ontrack = (event) => {
      const [remoteStream] = event.streams;
      this.callState.remoteStreams.set(userId, remoteStream);
      this.notifyStateChange();
    };

    // Handle ICE candidates
    peerConnection.onicecandidate = (event) => {
      if (event.candidate && this.callState.callId) {
        wsClient.sendIceCandidate(
          this.callState.callId,
          event.candidate.toJSON(),
          userId
        );
      }
    };

    // Handle connection state changes
    peerConnection.onconnectionstatechange = () => {
      console.log(`Peer connection state (${userId}):`, peerConnection.connectionState);
      if (
        peerConnection.connectionState === 'failed' ||
        peerConnection.connectionState === 'disconnected' ||
        peerConnection.connectionState === 'closed'
      ) {
        this.callState.peers.delete(userId);
        this.callState.remoteStreams.delete(userId);
        this.notifyStateChange();
      }
    };

    return peerConnection;
  }

  async createOffer(targetUserId: string): Promise<void> {
    if (!this.callState.localStream) {
      // Request media if not available
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true,
        });
        this.callState.localStream = stream;
        this.callState.isVideoEnabled = true;
        this.callState.isMuted = false;
      } catch (error) {
        throw new Error('Failed to get media stream');
      }
    }

    // Check if peer connection already exists
    let peerConnection = this.callState.peers.get(targetUserId);
    if (!peerConnection) {
      peerConnection = this.createPeerConnection(targetUserId);
      this.callState.peers.set(targetUserId, peerConnection);

      // Add local stream tracks
      this.callState.localStream.getTracks().forEach((track) => {
        peerConnection.addTrack(track, this.callState.localStream!);
      });
    }

    const offer = await peerConnection.createOffer();
    await peerConnection.setLocalDescription(offer);

    if (this.callState.callId) {
      wsClient.sendOffer(this.callState.callId, offer, targetUserId);
    } else {
      console.warn('CallId not available when creating offer');
    }
  }

  toggleMute(): void {
    if (this.callState.localStream) {
      this.callState.localStream.getAudioTracks().forEach((track) => {
        track.enabled = this.callState.isMuted;
      });
      this.callState.isMuted = !this.callState.isMuted;
      this.notifyStateChange();
    }
  }

  toggleVideo(): void {
    if (this.callState.localStream) {
      this.callState.localStream.getVideoTracks().forEach((track) => {
        track.enabled = this.callState.isVideoEnabled;
      });
      this.callState.isVideoEnabled = !this.callState.isVideoEnabled;
      this.notifyStateChange();
    }
  }

  endCall(): void {
    // Stop local stream
    if (this.callState.localStream) {
      this.callState.localStream.getTracks().forEach((track) => {
        track.stop();
      });
      this.callState.localStream = null;
    }

    // Close all peer connections
    this.callState.peers.forEach((peerConnection) => {
      peerConnection.close();
    });
    this.callState.peers.clear();
    this.callState.remoteStreams.clear();

    // Notify server if call is active
    if (this.callState.callId && this.callState.isCallActive) {
      wsClient.endCall(this.callState.callId);
    }

    // Reset state
    this.callState = {
      callId: null,
      isInCall: false,
      isCallActive: false,
      localStream: null,
      remoteStreams: new Map(),
      peers: new Map(),
      isMuted: false,
      isVideoEnabled: true,
      participants: new Map(),
    };

    this.notifyStateChange();
  }

  cleanup(): void {
    this.endCall();
    this.callStateListeners.clear();
  }
}

export const webrtcManager = new WebRTCManager();

